import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Param } from "./Param";
import { stepLabel } from "@/lib/music";

const pct = (v) => `${Math.round(v * 100)}%`;
const db = (v) => `${v > 0 ? "+" : ""}${v.toFixed(1)} dB`;
const panFmt = (v) => (Math.abs(v) < 0.02 ? "C" : `${v < 0 ? "L" : "R"}${Math.round(Math.abs(v) * 100)}`);
const st = (v) => `${v > 0 ? "+" : ""}${v} st`;

const Section = ({ title, children }) => (
  <div className="space-y-4 pt-4 border-t border-[#352d40] first:border-0 first:pt-0">
    <h4 className="text-xs font-mono-vox tracking-[0.2em] uppercase text-[#D99B43]">{title}</h4>
    {children}
  </div>
);

const TrackControls = ({ track, onUpdate, onReload }) => (
  <div className="space-y-5">
    <p className="font-gothic text-base text-amber-100/90 truncate" data-testid="channel-track-name">{track.name}</p>
    <Section title="Fader">
      <Param testId="track-volume-slider" label="Volume" value={track.volume} min={0} max={1.5} fmt={pct} onChange={(v) => onUpdate({ volume: v })} />
      <Param testId="track-pan-slider" label="Pan" value={track.pan} min={-1} max={1} fmt={panFmt} onChange={(v) => onUpdate({ pan: v })} />
      {track.mode === "diatonic" ? (
        <Param testId="track-steps-slider" label="In-key interval" value={track.degrees} min={-7} max={7} step={1} fmt={stepLabel} onChange={(v) => onUpdate({ degrees: v })} onCommit={(v) => onReload({ degrees: v })} />
      ) : (
        <Param testId="track-pitch-slider" label="Pitch" value={track.semitones} min={-12} max={12} step={1} fmt={st} onChange={(v) => onUpdate({ semitones: v })} onCommit={(v) => onReload({ semitones: v })} />
      )}
      <Param testId="track-sync-slider" label="Sync offset (earlier)" value={track.trim || 0} min={0} max={1} step={0.005} fmt={(v) => `${Math.round(v * 1000)} ms`} onChange={(v) => onUpdate({ trim: v })} onCommit={(v) => onReload({ trim: v })} />
    </Section>
    <Section title="3-Band EQ">
      <Param testId="eq-low-slider" label="Low · 120Hz" value={track.eq_low} min={-12} max={12} step={0.5} fmt={db} onChange={(v) => onUpdate({ eq_low: v })} />
      <Param testId="eq-mid-slider" label="Mid · 1kHz" value={track.eq_mid} min={-12} max={12} step={0.5} fmt={db} onChange={(v) => onUpdate({ eq_mid: v })} />
      <Param testId="eq-high-slider" label="High · 8kHz" value={track.eq_high} min={-12} max={12} step={0.5} fmt={db} onChange={(v) => onUpdate({ eq_high: v })} />
    </Section>
    <Section title="Sends">
      <Param testId="reverb-mix-slider" label="Cathedral Vault" value={track.reverb} min={0} max={1} fmt={pct} onChange={(v) => onUpdate({ reverb: v })} />
      <Param testId="delay-mix-slider" label="Crypt Echo" value={track.delay} min={0} max={1} fmt={pct} onChange={(v) => onUpdate({ delay: v })} />
    </Section>
  </div>
);

const MasterControls = ({ master, onMaster }) => (
  <div className="space-y-5">
    <Section title="Master Output">
      <Param testId="master-volume-slider" label="Volume" value={master.volume} min={0} max={1.5} fmt={pct} onChange={(v) => onMaster({ volume: v })} />
    </Section>
    <Section title="Cathedral Vault Reverb">
      <Param testId="reverb-size-slider" label="Decay" value={master.reverb_size} min={0.5} max={6} step={0.1} fmt={(v) => `${v.toFixed(1)} s`} onChange={(v) => onMaster({ reverb_size: v })} />
    </Section>
    <Section title="Crypt Echo Delay">
      <Param testId="delay-time-slider" label="Time" value={master.delay_time} min={0.05} max={1.2} fmt={(v) => `${Math.round(v * 1000)} ms`} onChange={(v) => onMaster({ delay_time: v })} />
      <Param testId="delay-feedback-slider" label="Feedback" value={master.delay_feedback} min={0} max={0.85} fmt={pct} onChange={(v) => onMaster({ delay_feedback: v })} />
    </Section>
  </div>
);

export const ChannelStrip = ({ track, master, onUpdate, onMaster, onReload }) => (
  <div data-testid="channel-strip-panel">
    <Tabs defaultValue="track">
      <TabsList className="w-full grid grid-cols-2 bg-[#0f0d11] rounded-none border border-[#352d40]">
        <TabsTrigger data-testid="tab-track" value="track" className="rounded-none font-mono-vox text-[11px] tracking-[0.2em] uppercase">Channel</TabsTrigger>
        <TabsTrigger data-testid="tab-master" value="master" className="rounded-none font-mono-vox text-[11px] tracking-[0.2em] uppercase">Master</TabsTrigger>
      </TabsList>
      <TabsContent value="track" className="pt-5">
        {track ? (
          <TrackControls track={track} onUpdate={onUpdate} onReload={onReload} />
        ) : (
          <p className="font-cormorant italic text-stone-500 text-lg">Select a voice to shape it.</p>
        )}
      </TabsContent>
      <TabsContent value="master" className="pt-5">
        <MasterControls master={master} onMaster={onMaster} />
      </TabsContent>
    </Tabs>
  </div>
);
